import type { AnyTFunction } from '@/i18n/msg';

/**
 * The crisis resources, as data (#2109 R3-B8; Spec 12 §7.1; promise P-01).
 *
 * Two numbers, the same on every surface and in every UI language (Victor,
 * 2026-10-14): 911 if the danger is immediate, otherwise 988. The separate
 * crisis text line is gone. Texting 988 reaches English and Spanish counselors
 * only, so the per-language route lives in the words around the numbers: es
 * says press 2 or text AYUDA, and zh, pt and fr say call 988 and name the
 * language for an interpreter (988 interprets on calls only).
 *
 * The numbers reach every `crisis:*` sentence as interpolation params, so no
 * translation can change, drop or reformat them, and nothing here is ever
 * AI-generated. `CrisisCallout`, `CrisisDisclosure` and `EscalationBanner` all
 * read this one object — the three used to carry drifting copies of the same
 * numbers. The ruling is in docs/DECISIONS.md (2026-10-14, crisis resources per
 * locale).
 */
export const CRISIS_RESOURCES = {
  /** 988 Suicide & Crisis Lifeline — call (every language) or text (en, es). */
  lifeline: '988',
  /** Emergency services, for danger happening now. */
  emergency: '911',
} as const;

/** The params every `crisis:*` sentence interpolates. */
export const CRISIS_PARAMS = {
  lifeline: CRISIS_RESOURCES.lifeline,
  emergency: CRISIS_RESOURCES.emergency,
} as const;

/** The five lines of the fixed safety template (`CrisisDisclosure`), in order. */
export const CRISIS_TEXT_LINE_KEYS = [
  'crisis:disclosure.lede',
  'crisis:disclosure.inDanger',
  'crisis:disclosure.lifeline',
  'crisis:disclosure.emergency',
  'crisis:disclosure.medicalTeam',
] as const;

type LooseT = (key: string, params?: Record<string, unknown>) => string;

/** The five lines, resolved with any `t` (the hook’s in the component, a fixed-language one in tests). */
export function crisisTextLines(t: AnyTFunction): string[] {
  return CRISIS_TEXT_LINE_KEYS.map((key) => (t as unknown as LooseT)(key, CRISIS_PARAMS));
}
